/**
 * 图片地址选择 helper（前端）
 *
 * 设计目标：
 * - 列表场景统一用缩略图，详情/Lightbox 主图用原图。
 * - 缩略图地址不入库，由原图 URL 推导得到，与后端 `thumbnailPath.ts` 保持一致。
 * - 后端在大部分接口出口已经补全 `thumbnailUrl` / `thumbnailMediumUrl` 字段；
 *   旧接口或漏接的地方走前端 fallback 推导，不会破坏。
 */

import { resolveBrowserMediaUrl } from './storageUrl';

export type ImageDisplaySize = 'small' | 'medium' | 'original';

interface DisplayWork {
  resultUrl?: string | null;
  thumbnailUrl?: string | null;
  thumbnailMediumUrl?: string | null;
}

const THUMB_EXT: Record<Exclude<ImageDisplaySize, 'original'>, string> = {
  small: '.s.webp',
  medium: '.m.webp',
};

const SUPPORTED_ORIGIN_EXT = /\.(png|jpg|jpeg|webp)$/i;
const THUMBNAIL_DIR = 'thumbs';

/** 由原图 URL 推导缩略图 URL；不可推导时返回 null。 */
export function deriveThumbnailUrl(
  resultUrl: string | null | undefined,
  size: Exclude<ImageDisplaySize, 'original'>,
): string | null {
  if (!resultUrl) return null;
  if (resultUrl.startsWith('data:')) return null;

  const parsed = splitProjectImageUrl(resultUrl);
  if (!parsed) return null;

  const { prefix, tail } = parsed;
  const parts = tail.split('/').filter(Boolean);
  if (parts.length === 0) return null;

  const filename = parts[parts.length - 1];
  const m = filename.match(SUPPORTED_ORIGIN_EXT);
  if (!m) return null;

  const base = filename.slice(0, filename.length - m[0].length);
  const newName = `${base}${THUMB_EXT[size]}`;
  const dirParts = parts.slice(0, -1);
  return `${prefix}${[...dirParts, THUMBNAIL_DIR, newName].join('/')}`;
}

function splitProjectImageUrl(
  value: string,
): { prefix: string; tail: string } | null {
  const marker = '/api/media/file/';

  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value);
      const idx = parsed.pathname.indexOf(marker);
      if (idx < 0) return null;
      const prefix = `${parsed.origin}${parsed.pathname.slice(0, idx + marker.length)}`;
      const tail = parsed.pathname.slice(idx + marker.length);
      return { prefix, tail };
    } catch {
      return null;
    }
  }

  const idx = value.indexOf(marker);
  if (idx < 0) return null;
  const prefix = value.slice(0, idx + marker.length);
  const tail = value.slice(idx + marker.length);
  return { prefix, tail };
}

/**
 * 根据展示场景挑出最合适的图片 URL。
 *
 * 顺序：
 * 1. 后端已带的对应字段（`thumbnailUrl` / `thumbnailMediumUrl` / `resultUrl`）。
 * 2. 由 `resultUrl` 前端推导。
 * 3. 回落 `resultUrl` 原图。
 * 4. 最后兜底原始 `resultUrl`。
 *
 * 返回 null 时调用方应显示占位。
 */
export function getDisplayUrl(
  work: DisplayWork | null | undefined,
  size: ImageDisplaySize = 'small',
): string | null {
  if (!work) return null;

  if (size === 'original') {
    return resolveBrowserMediaUrl(work.resultUrl || null);
  }

  const fromServer = size === 'small' ? work.thumbnailUrl : work.thumbnailMediumUrl;
  if (fromServer) return resolveBrowserMediaUrl(fromServer);

  const derived = deriveThumbnailUrl(work.resultUrl ?? null, size);
  if (derived) return derived;

  return resolveBrowserMediaUrl(work.resultUrl || null);
}

/**
 * 缩略图加载失败时回落到原图。供 `<img onError>` 使用：
 *
 *   <img src={getDisplayUrl(w, 'small')} onError={fallbackToOriginal(w)} />
 */
export function fallbackToOriginal(
  work: DisplayWork | null | undefined,
): React.ReactEventHandler<HTMLImageElement> {
  return (e) => {
    const target = e.currentTarget;
    if (!work) return;
    // 走统一入口归一化（剥 origin + 补令牌），不能直接用原始 resultUrl
    const fallback = resolveBrowserMediaUrl(work.resultUrl || null);
    if (!fallback) return;
    // target.src 是浏览器解析后的绝对地址，剥成相对形式再比较，避免回退失败时无限循环
    let current = target.src;
    try {
      const parsed = new URL(current);
      current = `${parsed.pathname}${parsed.search}`;
    } catch {
      // blob: / data: 等无法解析时保持原样
    }
    if (current === fallback) return; // 已经是原图，避免无限循环
    target.src = fallback;
  };
}
